import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const RISK_MAP = {
  critical: { label: 'Critico',  color: 'var(--red)', bg: 'var(--red-dim)', border: '#F5C0BC' },
  high:     { label: 'Alto',     color: '#D45D00', bg: 'var(--amber-dim)', border: '#F5C080' },
  medium:   { label: 'Medio',    color: 'var(--accent)', bg: '#E6F1FB', border: '#90C4E8' },
  low:      { label: 'Bajo',     color: 'var(--ink-2)', bg: 'var(--bg-subtle)', border: '#DDDDDD' },
};
const CATEGORY_MAP = {
  runtime:       { label: 'Runtime',        icon: '⚙' },
  remote_access: { label: 'Acceso remoto',  icon: '🖥' },
  credentials:   { label: 'Credenciales',   icon: '🔑' },
  gaming:        { label: 'Gaming',         icon: '🎮' },
  p2p:           { label: 'P2P',            icon: '⬇' },
  eol:           { label: 'EOL',            icon: '⏱' },
  hacking_tool:  { label: 'Hacking tool',   icon: '⚠' },
  tunnel:        { label: 'Tunnel',         icon: '🔗' },
  miner:         { label: 'Miner',          icon: '⛏' },
  network_tool:  { label: 'Red',            icon: '🌐' },
  utility:       { label: 'Utilidad',       icon: '🔧' },
  general:       { label: 'General',        icon: '📦' },
};
function RiskBadge({ level }) {
  const r = RISK_MAP[level] || RISK_MAP.low;
  return <span style={{ color: r.color, fontSize: 11, fontWeight: 500 }}>{r.label}</span>;
}
function KevBadge() {
  return <span style={{ color: '#F0997B', fontSize: 11, fontWeight: 700 }}>KEV</span>;
}
export default function Hygiene() {
  const [summary, setSummary] = useState(null);
  const [byAsset, setByAsset] = useState([]);
  const [bySoftware, setBySoftware] = useState([]);
  const [activeView, setActiveView] = useState('software');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [kevData, setKevData] = useState(null);
  const [kevLoading, setKevLoading] = useState(false);
  const [expandedKev, setExpandedKev] = useState({});
  const [dismissing, setDismissing] = useState({});
  const [kevSeverity, setKevSeverity] = useState('critical');   // por defecto solo críticos
  const [allowlist, setAllowlist] = useState([]);
  const [showAllow, setShowAllow] = useState(false);
  const [permitting, setPermitting] = useState({});
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/hygiene/summary`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/hygiene/by-asset`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/hygiene/by-software`, { headers }).then(r => r.json()),
    ]).then(([s, a, sw]) => {
      setSummary(s);
      setByAsset(Array.isArray(a) ? a : []);
      setBySoftware(Array.isArray(sw) ? sw : []);
    }).finally(() => setLoading(false));
  };

  const loadKev = (sev = kevSeverity) => {
    setKevLoading(true);
    const q = sev ? `?severity=${sev}` : '';
    Promise.all([
      fetch(`${API_URL}/nvd/summary`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/nvd/fleet${q}`, { headers }).then(r => r.json()).catch(() => ({})),
    ]).then(([summary, fleet]) => {
      setKevData({ ...(summary || {}), byAsset: fleet || {} });
    }).finally(() => setKevLoading(false));
  };

  const loadAllowlist = () => {
    fetch(`${API_URL}/hygiene/allowlist`, { headers }).then(r => r.json())
      .then(d => setAllowlist(Array.isArray(d) ? d : [])).catch(() => setAllowlist([]));
  };

  const permit = async (name) => {
    setPermitting(p => ({ ...p, [name]: true }));
    try {
      await fetch(`${API_URL}/hygiene/allowlist`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ software_name: name }),
      });
      loadAllowlist();
      load();   // el software permitido desaparece de la lista (salvo que tenga CVE activo)
    } catch (e) {}
    setPermitting(p => { const n = { ...p }; delete n[name]; return n; });
  };

  const removePermit = async (id) => {
    try {
      await fetch(`${API_URL}/hygiene/allowlist/${id}`, { method: 'DELETE', headers });
      loadAllowlist();
      load();
    } catch (e) {}
  };

  useEffect(() => { load(); loadAllowlist(); }, []);
  useEffect(() => { if (activeView === 'kev') loadKev(kevSeverity); }, [activeView, kevSeverity]);

  const handleDismiss = async (assetId, softwareName, cveId) => {
    const key = `${assetId}::${softwareName}::${cveId}`;
    setDismissing(p => ({ ...p, [key]: true }));
    try {
      await fetch(`${API_URL}/nvd/dismiss`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: assetId, software_name: softwareName, cve_id: cveId, reason: 'Falso positivo' }),
      });
      // Remove CVE from local state immediately
      setKevData(prev => {
        if (!prev?.byAsset) return prev;
        const updated = { ...prev, byAsset: { ...prev.byAsset } };
        const asset = updated.byAsset[assetId];
        if (!asset) return prev;
        const newSoftware = asset.affected_software.map(sw => {
          if (sw.software_name !== softwareName) return sw;
          return { ...sw, cves: sw.cves.filter(c => c.cve_id !== cveId) };
        }).filter(sw => sw.cves.length > 0);
        const newTotal = newSoftware.reduce((s, sw) => s + sw.cves.length, 0);
        if (newTotal === 0) {
          delete updated.byAsset[assetId];
        } else {
          updated.byAsset[assetId] = { ...asset, affected_software: newSoftware, total_cves: newTotal };
        }
        return updated;
      });
    } catch (e) {}
    setDismissing(p => { const n = {...p}; delete n[key]; return n; });
  };

  const handleAnalyze = () => {
    setAnalyzing(true);
    fetch(`${API_URL}/hygiene/analyze`, { method: 'POST', headers })
      .then(() => { setTimeout(() => { load(); setAnalyzing(false); }, 3000); })
      .catch(() => setAnalyzing(false));
  };

  const filtered = activeView === 'software'
    ? bySoftware.filter(s => (!search || s.software_name?.toLowerCase().includes(search.toLowerCase())) && (!filterRisk || s.risk_level === filterRisk))
    : byAsset.filter(a => !search || a.hostname?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Higiene de software</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Análisis de riesgos y vulnerabilidades activas (CISA KEV)</div>
        </div>
        <button className="btn btn-primary" onClick={handleAnalyze} disabled={analyzing}>
          {analyzing ? 'Analizando...' : 'Analizar ahora'}
        </button>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            ['Equipos analizados', summary.assets_analyzed, 'var(--accent)'],
            ['Equipos con riesgos', summary.assets_with_risks, '#D45D00'],
            ['Riesgos críticos', summary.critical, 'var(--red)'],
            ['Riesgos altos', summary.high, '#D45D00'],
            ['Software riesgoso', summary.unique_risky_software, 'var(--ink-2)'],
          ].map(([label, val, color]) => (
            <div key={label} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color }}>{val || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
          {[
            { key: 'software', label: 'Por software' },
            { key: 'asset',    label: 'Por equipo' },
            { key: 'kev',      label: '🛡 CVEs activos (KEV)' },
          ].map(v => (
            <button key={v.key} className={`btn ${activeView === v.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setActiveView(v.key); setSearch(''); setFilterRisk(''); }}>
              {v.label}
            </button>
          ))}
          {activeView !== 'kev' && (
            <>
              <input className="form-input" placeholder="Buscar..." value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: 200, padding: '6px 10px', marginLeft: 8 }} />
              {activeView === 'software' && (
                <select className="form-input" value={filterRisk} onChange={e => setFilterRisk(e.target.value)}
                  style={{ width: 130, padding: '6px 10px' }}>
                  <option value="">Todos</option>
                  {Object.entries(RISK_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              )}
              {activeView === 'software' && allowlist.length > 0 && (
                <button className="btn btn-secondary" onClick={() => setShowAllow(s => !s)}
                  style={{ marginLeft: 'auto' }}>
                  {showAllow ? 'Ocultar permitidos' : `Permitidos (${allowlist.length})`}
                </button>
              )}
            </>
          )}
          {activeView === 'kev' && (
            <select className="form-input" value={kevSeverity} onChange={e => setKevSeverity(e.target.value)}
              style={{ width: 230, padding: '6px 10px', marginLeft: 8 }}
              title="El feed KEV no trae CVSS; 'críticos' = KEV usados en ransomware">
              <option value="critical">Solo críticos (ransomware)</option>
              <option value="">Todos los CVE activos</option>
            </select>
          )}
        </div>

        {/* ── Panel de software permitido ── */}
        {activeView === 'software' && showAllow && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', background: 'var(--bg-subtle)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
              Software permitido — no aparece como riesgoso salvo que tenga un CVE activo
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {allowlist.map(a => (
                <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, background: 'var(--surface)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '4px 8px' }}>
                  {a.software_name}
                  <button onClick={() => removePermit(a.id)} title="Quitar de permitidos"
                    style={{ border: 'none', background: 'none', color: 'var(--red)', cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Vista KEV ── */}
        {activeView === 'kev' && (
          <div style={{ padding: 20 }}>
            {kevLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : kevData === null ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>
                Esta funcionalidad requiere el plan Enterprise.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    ['Software analizado', kevData.total_software, 'var(--accent)'],
                    ['Con CVEs KEV', kevData.with_cves, 'var(--red)'],
                    ['Total CVEs activos', kevData.kev_count, 'var(--red)'],
                    ['CVEs críticos', kevData.critical, '#D45D00'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--gray-100)' }}>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color }}>{val || 0}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--red)' }}>
                  <strong>⚠ Vulnerabilidades KEV</strong> — Estas vulnerabilidades están siendo explotadas activamente según CISA (Agencia de Ciberseguridad de EE.UU.). Requieren remediación inmediata.
                </div>

                {kevData.byAsset && Object.keys(kevData.byAsset).length > 0 ? (
                  Object.entries(kevData.byAsset).map(([assetId, data]) => {
                    const expanded = expandedKev[assetId];
                    return (
                      <div key={assetId} style={{ border: '1px solid #F5C0BC', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                        <div style={{ background: 'var(--red-dim)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => setExpandedKev(p => ({ ...p, [assetId]: !p[assetId] }))}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <KevBadge />
                            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--red)' }}>{data.hostname || assetId}</span>
                            <span style={{ fontSize: 12, color: '#888' }}>{data.user_name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>{data.total_cves} CVEs activos</span>
                            <span style={{ fontSize: 12, color: '#888' }}>{expanded ? '▲' : '▼'}</span>
                          </div>
                        </div>
                        {expanded && (
                          <div style={{ padding: '12px 16px' }}>
                            {data.affected_software.map((sw, i) => (
                              <div key={i} style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-900)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {sw.software_name}
                                  <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                                    {sw.versions && sw.versions.length > 1 ? `${sw.versions.length} versiones` : `v${sw.software_version}`}
                                  </span>
                                  <span style={{ fontSize: 11, background: '#F0F4F8', padding: '2px 6px', borderRadius: 4, color: '#666' }}>{sw.cves.length} CVEs</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {sw.cves.slice(0, 5).map((cve, j) => {
                                    const dismissKey = `${assetId}::${sw.software_name}::${cve.cve_id}`;
                                    return (
                                    <div key={j} style={{ background: 'var(--bg-subtle)', border: '1px solid #eee', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <a href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`} target="_blank" rel="noreferrer"
                                            style={{ fontWeight: 700, color: 'var(--red)', fontFamily: 'monospace', textDecoration: 'none' }}>
                                            {cve.cve_id} ↗
                                          </a>
                                          <KevBadge />
                                          {cve.cvss_score && (
                                            <span style={{
                                              background: cve.cvss_score >= 9 ? 'var(--red-dim)' : cve.cvss_score >= 7 ? 'var(--amber-dim)' : 'var(--bg-subtle)',
                                              color:      cve.cvss_score >= 9 ? 'var(--red)' : cve.cvss_score >= 7 ? '#D45D00' : 'var(--ink-2)',
                                              padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700
                                            }}>CVSS {cve.cvss_score}</span>
                                          )}
                                          {cve.cwe_id && (
                                            <span title={cve.cwe_name} style={{ background: '#F0F4F8', color: '#444', padding: '1px 6px', borderRadius: 4, fontSize: 10 }}>{cve.cwe_id}</span>
                                          )}
                                          {!cve.version_verified && (
                                            <span title="No se pudo verificar si esta versión está afectada" style={{ color: '#8B6800', fontSize: 10, fontWeight: 600 }}>sin verificar</span>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <span style={{ fontSize: 11, color: '#888' }}>Agregado: {cve.kev_date_added}</span>
                                          <button
                                            onClick={() => handleDismiss(assetId, sw.software_name, cve.cve_id)}
                                            disabled={!!dismissing[dismissKey]}
                                            title="Marcar como falso positivo"
                                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'var(--surface)', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            {dismissing[dismissKey] ? '...' : 'Falso positivo'}
                                          </button>
                                        </div>
                                      </div>
                                      <div style={{ color: '#555', lineHeight: 1.4 }}>{cve.description}</div>
                                    </div>
                                    );
                                  })}
                                  {sw.cves.length > 5 && (
                                    <div style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: 4 }}>
                                      + {sw.cves.length - 5} CVEs más
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>
                    No se detectaron vulnerabilidades KEV activas en los equipos.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Vista Software / Por equipo ── */}
        {activeView !== 'kev' && (
          <div className="table-wrap">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Sin resultados.</div>
            ) : activeView === 'software' ? (
              <table>
                <thead>
                  <tr>
                    <th>Software</th>
                    <th style={{ width: 100 }}>Riesgo</th>
                    <th style={{ width: 120 }}>Categoría</th>
                    <th style={{ width: 80 }}>Equipos</th>
                    <th>Motivos</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => {
                    const cat = CATEGORY_MAP[s.category] || CATEGORY_MAP.general;
                    return (
                      <tr key={i}>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{s.software_name}</div>
                          {s.version && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>v{s.version}</div>}
                          {s.is_eol && <span style={{ fontSize: 10, background: 'var(--amber-dim)', color: '#D45D00', padding: '1px 6px', borderRadius: 4 }}>EOL</span>}
                        </td>
                        <td><RiskBadge level={s.risk_level} /></td>
                        <td style={{ fontSize: 12 }}>{cat.icon} {cat.label}</td>
                        <td style={{ fontSize: 12 }}>
  {(s.hostnames || []).map((h, i) => (
    <span key={i} style={{ background: '#E6F1FB', color: 'var(--accent)', padding: '1px 6px', borderRadius: 4, fontSize: 11, marginRight: 4 }}>{h}</span>
  ))}
</td>
<td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
  {s.risk_reasons ? (Array.isArray(s.risk_reasons) ? s.risk_reasons.join(', ') : s.risk_reasons) : '—'}
</td>
<td>
  <button className="btn btn-secondary" disabled={!!permitting[s.software_name]}
    onClick={() => permit(s.software_name)}
    title="Marcar como permitido — deja de aparecer salvo que tenga un CVE activo"
    style={{ fontSize: 11, padding: '4px 8px', whiteSpace: 'nowrap' }}>
    {permitting[s.software_name] ? '…' : 'Permitir'}
  </button>
</td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Equipo</th>
                    <th style={{ width: 80 }}>Críticos</th>
                    <th style={{ width: 80 }}>Altos</th>
                    <th style={{ width: 80 }}>Medios</th>
                    <th style={{ width: 80 }}>Bajos</th>
                    <th style={{ width: 120 }}>Último scan</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a, i) => (
                    <tr key={i}>
                      <td>
                        <Link to={`/assets/${a.id}`} style={{ fontWeight: 500, fontSize: 13, color: 'var(--blue)' }}>{a.hostname}</Link>
                        {a.user_name && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{a.user_name}</div>}
                      </td>
                      <td style={{ textAlign: 'center' }}><span style={{ color: 'var(--red)', fontWeight: 700 }}>{a.critical || 0}</span></td>
                      <td style={{ textAlign: 'center' }}><span style={{ color: '#D45D00', fontWeight: 600 }}>{a.high || 0}</span></td>
                      <td style={{ textAlign: 'center' }}><span style={{ color: 'var(--accent)' }}>{a.medium || 0}</span></td>
                      <td style={{ textAlign: 'center' }}><span style={{ color: 'var(--ink-2)' }}>{a.low_count || 0}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.last_seen ? new Date(a.last_seen).toLocaleDateString('es-AR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}








